import { X, Copy, Check, Loader2, FlaskConical, BarChart3, History } from 'lucide-react';
import { useState, useEffect } from 'react';
import { SystemPrompt, SystemPromptUsageResponse } from '../types/systemPrompt';
import { ApiService } from '../services/apiService';

interface SystemPromptViewerDialogProps {
  prompt: SystemPrompt;
  versionLabel: string;
  scoreEntry?: { userScore: number | null; aiScore: number | null } | null;
  onClose: () => void;
}

export const SystemPromptViewerDialog = ({
  prompt,
  versionLabel,
  scoreEntry,
  onClose,
}: SystemPromptViewerDialogProps) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'content' | 'usage'>('content');
  const [usage, setUsage] = useState<SystemPromptUsageResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(prompt.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (activeTab === 'usage' && !usage && !usageLoading) {
      setUsageLoading(true);
      setUsageError(null);
      ApiService.getInstance()
        .getSystemPromptUsage(prompt.hash)
        .then(setUsage)
        .catch(err => setUsageError(err.message || 'Failed to load usage'))
        .finally(() => setUsageLoading(false));
    }
  }, [activeTab, usage, usageLoading, prompt.hash]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]" onClick={onClose} />
      <div
        data-component="SystemPromptViewerDialog"
        className="fixed inset-4 md:inset-10 bg-white rounded-xl shadow-2xl z-[70] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-semibold text-gray-900">{prompt.agentType}</span>
            <span className="text-sm text-gray-500">{versionLabel}</span>
            <code className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">
              {prompt.hash.slice(0, 8)}
            </code>
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded ${
                prompt.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {prompt.isActive ? 'Active' : 'Inactive'}
            </span>
            {scoreEntry?.userScore != null && (
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800">
                User: {scoreEntry.userScore.toFixed(1)}/10
              </span>
            )}
            {scoreEntry?.aiScore != null && (
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800">
                AI: {scoreEntry.aiScore.toFixed(1)}/10
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
          <button
            onClick={() => setActiveTab('content')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'content'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Prompt Content
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'usage'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Usage
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'content' && (
            <pre data-flow="content" className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
              {prompt.content}
            </pre>
          )}

          {activeTab === 'usage' && (
            <div data-flow="usage">
              {usageLoading && (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
              )}

              {usageError && (
                <div className="text-red-600 text-sm">{usageError}</div>
              )}

              {usage && !usageLoading && (
                <div className="space-y-6">
                  {/* Evaluation Tests */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FlaskConical className="w-4 h-4 text-gray-500" />
                      <h3 className="font-medium text-gray-900">
                        Evaluation Tests
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          ({usage.evaluationTests.length})
                        </span>
                      </h3>
                    </div>
                    {usage.evaluationTests.length === 0 ? (
                      <p className="text-sm text-gray-400 ml-6">No evaluation tests use this prompt</p>
                    ) : (
                      <div className="space-y-2 ml-6">
                        {usage.evaluationTests.map(test => (
                          <div
                            key={test.id}
                            className="border border-gray-200 rounded-lg px-4 py-3"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900 text-sm">{test.name}</span>
                              {test.testOrigin && (
                                <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                  {test.testOrigin}
                                </span>
                              )}
                              {test.averageUserScore != null && (
                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded font-medium">
                                  User: {test.averageUserScore.toFixed(1)}/10
                                </span>
                              )}
                              {test.averageAIScore != null && (
                                <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-800 rounded font-medium">
                                  AI: {test.averageAIScore.toFixed(1)}/10
                                </span>
                              )}
                            </div>
                            {test.aiBreakdown && (
                              <div className="flex items-center gap-2 mb-1 ml-0">
                                <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">
                                  Acc: {Math.round(test.aiBreakdown.accuracy * 100)}%
                                </span>
                                <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">
                                  Cit: {Math.round(test.aiBreakdown.citationQuality * 100)}%
                                </span>
                                <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">
                                  Comp: {Math.round(test.aiBreakdown.completeness * 100)}%
                                </span>
                                <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">
                                  Legal: {Math.round(test.aiBreakdown.legalQuality * 100)}%
                                </span>
                              </div>
                            )}
                            <p className="text-xs text-gray-500 mb-1">{test.purpose}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                              <span>{formatDate(test.createdAt)}</span>
                              <span>Agents: {test.matchingAgents.join(', ')}</span>
                              {test.userScoredExecutionCount > 0 && (
                                <span>{test.userScoredExecutionCount} user-scored</span>
                              )}
                              {test.aiScoredExecutionCount > 0 && (
                                <span>{test.aiScoredExecutionCount} AI-scored</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Scoring Runs */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 className="w-4 h-4 text-gray-500" />
                      <h3 className="font-medium text-gray-900">
                        Scoring Validation Runs
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          ({usage.scoringRuns.length})
                        </span>
                      </h3>
                    </div>
                    {usage.scoringRuns.length === 0 ? (
                      <p className="text-sm text-gray-400 ml-6">No scoring runs use this prompt</p>
                    ) : (
                      <div className="space-y-2 ml-6">
                        {usage.scoringRuns.map(run => (
                          <div
                            key={run.id}
                            className="border border-gray-200 rounded-lg px-4 py-3"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900 text-sm">{run.name}</span>
                              <span
                                className={`px-1.5 py-0.5 text-xs rounded ${
                                  run.status === 'Completed'
                                    ? 'bg-green-100 text-green-800'
                                    : run.status === 'Running'
                                      ? 'bg-blue-100 text-blue-800'
                                      : run.status === 'Failed'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {run.status}
                              </span>
                              {run.aiScoreAverage != null && (
                                <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-800 rounded font-medium">
                                  AI Avg: {run.aiScoreAverage.toFixed(1)}/10
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                              <span>{formatDate(run.createdAt)}</span>
                              <span>
                                {run.completedExecutions}/{run.totalExecutions} executions
                              </span>
                              {run.meanAbsoluteError != null && (
                                <span>MAE: {run.meanAbsoluteError.toFixed(2)}</span>
                              )}
                              {run.pearsonCorrelation != null && (
                                <span>Pearson: {run.pearsonCorrelation.toFixed(2)}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Standalone Executions */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <History className="w-4 h-4 text-gray-500" />
                      <h3 className="font-medium text-gray-900">
                        Standalone Executions
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          ({usage.standaloneExecutions?.length ?? 0})
                        </span>
                      </h3>
                    </div>
                    {!usage.standaloneExecutions?.length ? (
                      <p className="text-sm text-gray-400 ml-6">No standalone executions use this prompt</p>
                    ) : (
                      <div className="space-y-2 ml-6">
                        {usage.standaloneExecutions.map(exec => (
                          <div
                            key={exec.executionId}
                            className="border border-gray-200 rounded-lg px-4 py-3"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900 text-sm truncate max-w-md">
                                {exec.userCommand}
                              </span>
                              <span
                                className={`px-1.5 py-0.5 text-xs rounded ${
                                  exec.status === 'Completed'
                                    ? 'bg-green-100 text-green-800'
                                    : exec.status === 'Failed'
                                      ? 'bg-red-100 text-red-800'
                                      : exec.status === 'InProgress'
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {exec.status}
                              </span>
                              {exec.userScore != null && (
                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded font-medium">
                                  Score: {exec.userScore.toFixed(1)}/10
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                              <span>{formatDate(exec.startTime)}</span>
                              <span>{(exec.totalExecutionTimeMs / 1000).toFixed(1)}s</span>
                              {exec.userId && <span>{exec.userId}</span>}
                              <code className="font-mono text-gray-400">{exec.executionId.slice(0, 8)}</code>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Execution History Scores */}
                  {usage.historyUserScoredCount > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="w-4 h-4 text-gray-500" />
                        <h3 className="font-medium text-gray-900">
                          Execution History Scores
                        </h3>
                      </div>
                      <div className="ml-6 border border-gray-200 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded font-medium">
                            User Avg: {usage.historyAverageUserScore?.toFixed(1)}/10
                          </span>
                          <span className="text-xs text-gray-500">
                            from {usage.historyUserScoredCount} scored execution{usage.historyUserScoredCount > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
